import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaMapMarkerAlt, FaDirections } from 'react-icons/fa';
import { mergeAddressLines } from '../../utils/address';

const RestaurantLocation = ({ restaurant }) => {
  const { t } = useTranslation();
  if (!restaurant) return null;

  const fullAddress = mergeAddressLines(restaurant.address, restaurant.city);
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

  const lat = restaurant.latitude != null ? Number(restaurant.latitude) : null;
  const lng = restaurant.longitude != null ? Number(restaurant.longitude) : null;
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  // Petite bbox autour du point (~1km) pour un zoom raisonnable sur la carte OSM embarquée
  const delta = 0.01;
  const osmEmbedUrl = hasCoords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lng - delta}%2C${lat - delta}%2C${lng + delta}%2C${lat + delta}&layer=mapnik&marker=${lat}%2C${lng}`
    : null;

  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div style={{ width: '32px', height: '32px', borderRadius: '999px', background: '#054625', display: 'grid', placeItems: 'center' }}>
          <FaMapMarkerAlt size={15} color="#FBF9D9" />
        </div>
        <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(22px, 2.6vw, 28px)', letterSpacing: '-0.02em' }}>{t('restaurant.location')}</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 260px)', gap: '16px', alignItems: 'stretch' }}>
        {/* Adresse */}
        <div style={{ background: '#FCFBF5', borderRadius: '22px', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.5)' }}>{t('restaurant.address')}</span>
          <p style={{ margin: 0, fontSize: '16px', color: '#051004' }}>{fullAddress}</p>
          {(restaurant.postal_code || restaurant.country) && (
            <p style={{ margin: 0, fontSize: '13px', color: 'rgba(5,16,4,0.5)' }}>
              {[restaurant.postal_code, restaurant.country].filter(Boolean).join(' · ')}
            </p>
          )}
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center transition-colors"
            style={{ alignSelf: 'flex-start', marginTop: '6px', gap: '8px', background: '#054625', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', padding: '11px 20px', borderRadius: '999px' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#04381f'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#054625'; }}
          >
            <FaDirections size={14} />
            {t('restaurant.directions')}
          </a>
        </div>

        {/* Petite carte, en aside */}
        {hasCoords ? (
          <div style={{ borderRadius: '22px', overflow: 'hidden', minHeight: '160px' }}>
            <iframe
              title={t('restaurant.restaurantLocationTitle')}
              src={osmEmbedUrl}
              className="w-full h-full"
              style={{ border: 0, minHeight: '160px' }}
              loading="lazy"
            />
          </div>
        ) : (
          <div style={{ background: '#FBF9D9', borderRadius: '22px', minHeight: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <div className="text-center">
              <FaMapMarkerAlt size={28} style={{ color: 'rgba(5,16,4,0.25)', margin: '0 auto 8px' }} />
              <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>{t('restaurant.mapUnavailable')}</p>
              <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: 'rgba(5,16,4,0.45)' }}>{t('restaurant.noGpsCoordinates')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RestaurantLocation;
